// Routes and the frame. Board and bench are chrome-free (no menu, no toolbar);
// everything else sits in Ionic's split pane. Nothing renders until /api/me
// answers; a 401 shows the sign-in page.
import { IonApp, IonRouterOutlet, IonSplitPane, setupIonicReact } from "@ionic/react";
import { IonReactRouter } from "@ionic/react-router";
import { Redirect, Route, useLocation } from "react-router-dom";
import { ChangesPoller, MeProvider, useMe } from "./api/live";
import { Menu } from "./shell/Menu";
import { Loading } from "./shell/ui";
import { ErrorBoundary, guarded } from "./shell/ErrorBoundary";
import { logout } from "./auth/auth";
import { SignInPage } from "./pages/SignInPage";
import { AccountPage } from "./pages/AccountPage";
import { WeekPage } from "./pages/WeekPage";
import { BoardPage } from "./pages/BoardPage";
import { BenchPage } from "./pages/BenchPage";
import { ShelvesPage } from "./pages/ShelvesPage";
import { TasksPage } from "./pages/TasksPage";
import { StatsPage } from "./pages/StatsPage";
import { SkillsPage } from "./pages/SkillsPage";
import { ReviewPage } from "./pages/ReviewPage";
import { SettingsPage } from "./pages/SettingsPage";

setupIonicReact({ mode: "ios" });

// Each screen in its own error boundary: one bad answer can't blank the app.
const Screens = {
  WeekPage: guarded("Week", WeekPage), BoardPage: guarded("board", BoardPage), BenchPage: guarded("bench", BenchPage),
  ShelvesPage: guarded("Shelves", ShelvesPage), TasksPage: guarded("Build flow", TasksPage), StatsPage: guarded("Stats", StatsPage),
  SkillsPage: guarded("Skills", SkillsPage), ReviewPage: guarded("Review", ReviewPage), SettingsPage: guarded("Settings", SettingsPage)
};

const CHROME_FREE = ["/board", "/bench"];

function Frame() {
  const { pathname } = useLocation();
  const { status, error, reload } = useMe();
  if (pathname.startsWith("/account")) return <AccountPage />;
  if (status === "loading") return <Loading what="Opening the planner" />;
  if (status === "out") return <SignInPage returnTo={pathname} />;
  if (status === "denied") return (
    <div className="signin"><div className="card"><h1>No access to the planner</h1><p>{error ?? "Your OnlyCat account doesn't have access to the production planner."} Ask Andrea or James to add you, or sign in with another account.</p>
      <button type="button" className="btn btn--primary btn--lg" onClick={reload}>Try again</button>{" "}
      <button type="button" className="btn btn--lg" onClick={() => { logout().finally(reload); }}>Sign out</button></div></div>
  );
  if (status === "error") return (
    <div className="signin"><div className="card"><h1>The planner can't be reached</h1><p>{error}</p>
      <button type="button" className="btn btn--primary btn--lg" onClick={reload}>Try again</button></div></div>
  );
  const bare = CHROME_FREE.some((p) => pathname.startsWith(p));
  return (
    <>
      <ChangesPoller />
      <IonSplitPane contentId="main" when="lg" disabled={bare}>
        <Menu />
        <IonRouterOutlet id="main">
          <Route path="/week" exact component={Screens.WeekPage} />
          <Route path="/board" exact component={Screens.BoardPage} />
          <Route path="/bench" exact component={Screens.BenchPage} />
          <Route path="/shelves" exact component={Screens.ShelvesPage} />
          <Route path="/tasks" exact component={Screens.TasksPage} />
          <Route path="/stats" exact component={Screens.StatsPage} />
          <Route path="/skills" exact component={Screens.SkillsPage} />
          <Route path="/review" exact component={Screens.ReviewPage} />
          <Route path="/settings" exact component={Screens.SettingsPage} />
          <Route path="/" exact render={() => <Redirect to="/week" />} />
        </IonRouterOutlet>
      </IonSplitPane>
    </>
  );
}

export default function App() {
  return (
    <IonApp>
      <ErrorBoundary>
        <MeProvider>
          <IonReactRouter>
            <Frame />
          </IonReactRouter>
        </MeProvider>
      </ErrorBoundary>
    </IonApp>
  );
}
